import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  TablePagination,
  TableSortLabel,
} from "@mui/material";

import dayjs from "dayjs";
import { Link, Navigate } from "react-router-dom";

import ImageZoom from "../../components/ImageZoom/ImageZoom";
import * as actions from "../../redux/actions/index";
import PageLoading from "../../components/PageLoading/PageLoading.component";
import Filter from "../../components/Filter/Filter.component";
import { TablePaginationActions } from "../../components/TablePaginationActions/TablePaginationActions.component";
import { getSessionStorageItem } from "../../utils/getSessionStorageItem";
import { FIRST_PAGE, PAGE_SIZE_OPTIONS } from "../../utils/paginationConstants";

const Ads = () => {
  const dispatch = useDispatch();
  const cmsAuth = useSelector((state) => state.cmsAuthenticator.cmsAuth || "");
  const ads = useSelector((state) => state.cmsAdClicks.clicks || []);
  const total = useSelector((state) => state.cmsAdClicks.total || 0);
  const loading = useSelector((state) => state.cmsAdClicks.fetchAdsLoading);
  const filtersLoading = useSelector(
    (state) => state.cmsAdClicks.filtersLoading
  );
  const filtersApplied = useSelector(
    (state) => state.cmsAdClicks.filtersApplied
  );
  const filtersReset = useSelector((state) => state.cmsAdClicks.filtersReset);

  const [page, setPage] = useState(FIRST_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);
  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const storedParams = getSessionStorageItem("ads_filterParams");
  const isLoadingRef = useRef(loading);
  const isFiltersReset = useRef(filtersReset);

  // Update the ref whenever loading changes
  useEffect(() => {
    isLoadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    isFiltersReset.current = filtersReset;
  }, [filtersReset]);

  useEffect(() => {
    const fetchData = (params) => {
      dispatch(
        actions.onFetchAds(
          FIRST_PAGE,
          rowsPerPage,
          (order === "asc" ? "" : "-") + orderBy,
          params
        )
      );
    };

    if (filtersApplied && storedParams && !isLoadingRef.current) {
      fetchData(storedParams);
    } else if (!filtersApplied && !filtersLoading && !isFiltersReset.current) {
      fetchData(storedParams || null);
    }
  }, [
    dispatch,
    rowsPerPage,
    order,
    orderBy,
    filtersApplied,
    filtersLoading,
    storedParams,
  ]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);

    const params = storedParams
      ? [
          newPage,
          rowsPerPage,
          (order === "asc" ? "" : "-") + orderBy,
          storedParams,
        ]
      : [newPage, rowsPerPage, (order === "asc" ? "" : "-") + orderBy];
    dispatch(actions.onFetchAds(...params));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(FIRST_PAGE);

    const params = storedParams
      ? [
          FIRST_PAGE,
          newRowsPerPage,
          FIRST_PAGE,
          rowsPerPage,
          (order === "asc" ? "" : "-") + orderBy,
          storedParams,
        ]
      : [FIRST_PAGE, newRowsPerPage, (order === "asc" ? "" : "-") + orderBy];
    dispatch(actions.onFetchAds(...params));
  };

  const createSortHandler = (cellId) => {
    // Same cell, flip order
    if (cellId === orderBy) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      // Different cell, reset sort to ascending
      setOrderBy(cellId);
      setOrder("asc");
    }
  };

  const headCells = [
    {
      id: "fb_id",
      label: "FB ID",
      sortable: false,
    },
    {
      id: "ref",
      label: "Ref",
      sortable: false,
    },

    {
      id: "created_at",
      label: "Date Clicked",
      sortable: true,
    },

    {
      id: "ad_title",
      label: "Ad Name",
      sortable: false,
    },

    {
      id: "photo_url",
      label: "Image",
      sortable: false,
    },
  ];
  const restrict = cmsAuth.role !== "SUPER_ADMIN" && (
    <Navigate to="/dashboard" />
  );
  return (
    <>
      {restrict}
      <Filter page="ads" />
      <PageLoading loading={loading} />

      <div className="Ads wrapper">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => {
                  if (headCell.sortable) {
                    return (
                      <TableCell
                        key={headCell.id}
                        sortDirection={orderBy === headCell.id ? order : false}
                        align="center"
                      >
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={orderBy === headCell.id ? order : "asc"}
                          onClick={() => createSortHandler(headCell.id)}
                        >
                          {headCell.label}
                        </TableSortLabel>
                      </TableCell>
                    );
                  } else {
                    return (
                      <TableCell key={headCell.id} align="center">
                        {headCell.label}
                      </TableCell>
                    );
                  }
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {ads.map((click, i) => {
                const clickDateFormatted = click.created_at
                  ? dayjs(click.created_at).format("MM/DD/YYYY h:mm A")
                  : null;
                return (
                  <TableRow key={i} hover>
                    <TableCell align="center" component={Link}>
                      {click.fb_id}
                    </TableCell>

                    <TableCell align="center" component={Link}>
                      {click.ref}
                    </TableCell>

                    <TableCell align="center" component={Link} width={"200px"}>
                      {clickDateFormatted}
                    </TableCell>

                    <TableCell align="center" component={Link}>
                      {click.ad_title}
                    </TableCell>

                    <TableCell align="center">
                      <ImageZoom key={click.photo_url} src={click.photo_url} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                  colSpan={14}
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      "aria-label": "rows per page",
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default Ads;
